import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { findDepartementFieldRaw } from "@/lib/departementDisplay";
import { formatPhoneInputLive, isPhoneLikeChampName } from "@/lib/phoneFormat";
import {
  buildAnnonceEditPayload,
  editChampsMasquesRechercheList,
  filterEditChampsDynamiques,
  initAnnonceEditForm,
  normalizeAnnonceType,
  resolveEditDeptChampKey,
} from "@/lib/annonceEdit";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Modale d’édition d’annonce (vendeur ou admin).
 * @param {{ open: boolean, dossier: object|null, onOpenChange: (open: boolean) => void, onSaved?: () => void|Promise<void>, savePut: (dossierId: string, payload: object) => Promise<unknown>, title?: string }} props
 */
export default function AnnonceEditDialog({
  open,
  dossier,
  onOpenChange,
  onSaved,
  savePut,
  title = "Modifier l’annonce",
}) {
  const [form, setForm] = useState(() => initAnnonceEditForm(null));
  const [produits, setProduits] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/produits")
      .then(({ data }) => setProduits(Array.isArray(data) ? data : []))
      .catch(() => setProduits([]));
  }, []);

  useEffect(() => {
    if (open && dossier) {
      setForm(initAnnonceEditForm(dossier));
    }
  }, [open, dossier]);

  const secteurs = useMemo(
    () => [...new Set(produits.map((p) => p?.secteur).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")),
    [produits],
  );

  const produitsFiltres = useMemo(() => {
    if (!form?.secteur) return [];
    return produits.filter((p) => p?.secteur === form.secteur);
  }, [produits, form?.secteur]);

  const produitSelectionne = useMemo(
    () => produits.find((p) => String(p?.produit_id) === String(form?.produit_id || "")),
    [produits, form?.produit_id],
  );

  const deptChampKey = useMemo(
    () => resolveEditDeptChampKey(produitSelectionne, form?.type),
    [produitSelectionne, form?.type],
  );

  const champsDynamiques = useMemo(
    () => filterEditChampsDynamiques(produitSelectionne, form, deptChampKey),
    [produitSelectionne, form, deptChampKey],
  );

  const champsMasquesRecherche = useMemo(
    () => editChampsMasquesRechercheList(produitSelectionne, form?.type),
    [produitSelectionne, form?.type],
  );

  useEffect(() => {
    if (!open || !dossier || !deptChampKey) return;
    if (normalizeAnnonceType(form?.type) !== "recherche") return;
    setForm((p) => {
      const cf = p.custom_fields && typeof p.custom_fields === "object" ? { ...p.custom_fields } : {};
      if (cf[deptChampKey] != null && String(cf[deptChampKey]).trim() !== "") return p;
      const { raw } = findDepartementFieldRaw(dossier);
      if (!raw) return p;
      return { ...p, custom_fields: { ...cf, [deptChampKey]: raw } };
    });
  }, [open, dossier, deptChampKey, form?.type]);

  const dossierId = dossier?.dossier_id || dossier?.id;

  const handleSave = async () => {
    if (!dossierId || !savePut) return;
    const payload = buildAnnonceEditPayload(form, {
      editingDossier: dossier,
      editProduitSelectionne: produitSelectionne,
      editDeptChampKey: deptChampKey,
    });
    setSaving(true);
    try {
      await savePut(String(dossierId), payload);
      toast.success("Annonce enregistrée");
      onOpenChange(false);
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Modification impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onOpenChange(v)}>
      <DialogContent
        data-testid="annonce-edit-dialog"
        className="flex max-h-[85vh] w-full max-w-[min(calc(100vw-2rem),42rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <DialogHeader className="border-b border-slate-100 px-6 py-4 pr-12">
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-6 py-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select value={form.type || "vente"} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Type d’annonce" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vente">Ce que je vends</SelectItem>
                <SelectItem value="recherche">Ce que j’achète</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={form.secteur || ""}
              onValueChange={(v) => setForm((p) => ({ ...p, secteur: v, produit_id: "", custom_fields: {} }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Secteur" />
              </SelectTrigger>
              <SelectContent>
                {secteurs.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select
            value={form.produit_id || ""}
            onValueChange={(v) => setForm((p) => ({ ...p, produit_id: v, custom_fields: {} }))}
            disabled={!form.secteur}
          >
            <SelectTrigger>
              <SelectValue placeholder={form.secteur ? "Produit" : "Choisir d’abord le secteur"} />
            </SelectTrigger>
            <SelectContent>
              {produitsFiltres.map((p) => (
                <SelectItem key={p.produit_id} value={p.produit_id}>
                  {p.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {champsDynamiques.length > 0 ? (
            <div className="space-y-2 rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Champs de qualification</p>
              {champsDynamiques.map((c) => {
                const key = String(c.nom);
                const value = form.custom_fields?.[key];
                const listOptions = Array.isArray(c.options)
                  ? c.options.map((o) => String(o).trim()).filter(Boolean)
                  : [];
                return (
                  <div key={c.champ_id ?? key} className="space-y-1.5">
                    <p className="text-sm font-medium text-slate-800">
                      {key}
                      {c.visible_public === false ? (
                        <span className="ml-1.5 text-[11px] font-semibold text-amber-700">(masqué marketplace)</span>
                      ) : null}
                    </p>
                    {c.type === "list" ? (
                      <Select
                        value={value != null && value !== "" ? String(value) : ""}
                        onValueChange={(v) =>
                          setForm((p) => ({
                            ...p,
                            custom_fields: { ...(p.custom_fields || {}), [key]: v },
                          }))
                        }
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Choisir" />
                        </SelectTrigger>
                        <SelectContent>
                          {listOptions.map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : c.type === "boolean" ? (
                      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <Switch
                          checked={Boolean(value)}
                          onCheckedChange={(v) =>
                            setForm((p) => ({
                              ...p,
                              custom_fields: { ...(p.custom_fields || {}), [key]: v },
                            }))
                          }
                        />
                        <span className="text-sm text-slate-700">{value ? "Oui" : "Non"}</span>
                      </div>
                    ) : c.type === "number" && isPhoneLikeChampName(key) ? (
                      <Input
                        type="tel"
                        inputMode="tel"
                        value={value ?? ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            custom_fields: {
                              ...(p.custom_fields || {}),
                              [key]: formatPhoneInputLive(e.target.value),
                            },
                          }))
                        }
                        className="bg-white font-mono tabular-nums"
                      />
                    ) : (
                      <Input
                        type="text"
                        inputMode={c.type === "number" ? "decimal" : undefined}
                        value={value ?? ""}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            custom_fields: { ...(p.custom_fields || {}), [key]: e.target.value },
                          }))
                        }
                        className={c.type === "number" ? "bg-white font-mono tabular-nums" : "bg-white"}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
          {champsMasquesRecherche.length > 0 ? (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/70 px-3 py-2.5 text-xs text-amber-900">
              Champs masqués (non remplissables en recherche) : {champsMasquesRecherche.join(", ")}
            </div>
          ) : null}
          <Input
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Titre"
          />
          <Textarea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Description"
            rows={4}
          />
          {normalizeAnnonceType(form.type) === "recherche" ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                type="text"
                inputMode="decimal"
                value={form.price_min}
                onChange={(e) => setForm((p) => ({ ...p, price_min: e.target.value }))}
                placeholder="Budget min (€)"
                className="font-mono tabular-nums"
              />
              <Input
                type="text"
                inputMode="decimal"
                value={form.price_max}
                onChange={(e) => setForm((p) => ({ ...p, price_max: e.target.value }))}
                placeholder="Budget max (€)"
                className="font-mono tabular-nums"
              />
            </div>
          ) : (
            <Input
              type="text"
              inputMode="decimal"
              value={form.price}
              onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
              placeholder="Prix (€)"
              className="font-mono tabular-nums"
            />
          )}
          {normalizeAnnonceType(form.type) === "recherche" && deptChampKey ? (
            <div className="space-y-1">
              <Input
                value={form.custom_fields?.[deptChampKey] ?? ""}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    custom_fields: { ...(p.custom_fields || {}), [deptChampKey]: e.target.value },
                  }))
                }
                placeholder="Départements : 63, 43, 03 ou 63-43-03"
                className="font-mono tabular-nums"
              />
              <p className="text-[11px] text-slate-500">
                Plusieurs départements possibles, séparés par des virgules, espaces ou tirets.
              </p>
            </div>
          ) : null}
          {normalizeAnnonceType(form.type) !== "recherche" || !deptChampKey ? (
            <Input
              value={form.location}
              onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
              placeholder="Localisation"
            />
          ) : null}
          {normalizeAnnonceType(form.type) !== "recherche" ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                value={form.client_name}
                onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))}
                placeholder="Nom du client/prospect"
              />
              <Input
                type="tel"
                inputMode="tel"
                value={form.client_phone}
                onChange={(e) =>
                  setForm((p) => ({ ...p, client_phone: formatPhoneInputLive(e.target.value) }))
                }
                placeholder="Téléphone du client/prospect"
                className="font-mono tabular-nums"
              />
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t border-slate-200/80 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button className="btn-primary" onClick={() => void handleSave()} disabled={saving || !dossierId}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
